import React, { useState, useEffect, useRef, useMemo } from 'react';
import TableToolbar from '../../../../components/data-tables/TableToolbar';
import DataTable from './DataTable';
import { ColumnData, FixedColumnData, ESPItem } from '../../RealtimeInsightsSlice';
import Loader from '../../../common/Loader/Loader';

export default function RealTimeWidget({
  columnObjects,
  fixedColumnObjects,
  allRows,
  onExportXLS,
  isLoading,
}: {
  columnObjects: ColumnData[];
  fixedColumnObjects: FixedColumnData[];
  allRows: ESPItem[];
  onExportXLS?: () => void;
  isLoading: boolean;
}) {
  const dataTableRef = useRef<HTMLDivElement>(null);

  const sortedColumnObjects = useMemo(() => {
    return [...columnObjects].sort((a, b) => a.order - b.order);
  }, [columnObjects]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [applyTrigger, setApplyTrigger] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstallNumber] = useState<number | null>(null);
  const [columnObjectsState, setColumnObjectsState] = useState(columnObjects);
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);

  const rPermission = {
    canExport: true,
    canConfigureColumns: true,
  };

  // Initialize visible columns from sortedColumnObjects
  useEffect(() => {
    if (sortedColumnObjects && sortedColumnObjects.length > 0) {
      const columns = sortedColumnObjects.map((col: ColumnData) => col.key).filter((key: string) => key !== 'Info');
      setAllColumnsForToolbar(columns);
      const visibleKeys = sortedColumnObjects
        .filter((obj: ColumnData) => obj.selected)
        .map((obj: ColumnData) => obj.key)
        .filter((key: string) => key !== 'Info');
      setVisibleColumns(visibleKeys);
      setApplyTrigger(true);
      setTimeout(() => {
        setApplyTrigger(false);
      }, 1000);
    }
  }, [columnObjects, sortedColumnObjects]);

  const fixedColumns = ['wellName', 'status'];

  // Update column objects state when columnObjects change
  useEffect(() => {
    if (JSON.stringify(columnObjects) !== JSON.stringify(columnObjectsState)) {
      setColumnObjectsState(columnObjects);
    }
  }, [columnObjects, columnObjectsState]);

  // Create dynamic header map from column objects
  const dynamicHeaderMap = columnObjects.reduce(
    (acc: Record<string, string>, col: ColumnData) => {
      acc[col.key] = col.label;
      return acc;
    },
    {} as Record<string, string>,
  );

  const fullHeaderMap: Record<string, string> = {
    Icon: '',
    Date: 'Date',
    ...dynamicHeaderMap,
  };

  function getColumnHeader(columnId: string): string {
    return (fullHeaderMap && fullHeaderMap[columnId]) || columnId;
  }

  const handleExportXLS = () => {
    if (onExportXLS) {
      onExportXLS();
    }
  };

  const handleUpdateColumns = (updated: { id: string; Header: string; isVisible: boolean }[]) => {
    const newVisible = updated.filter((col) => col.isVisible).map((col) => col.id);
    setVisibleColumns(newVisible);
  };

  const handleColumnToggle = (columnId: string) => {
    setVisibleColumns((prev) => {
      let updatedColumns = [...prev];
      if (updatedColumns.includes(columnId)) {
        if (updatedColumns.filter((id) => !fixedColumns.includes(id)).length === 1) return updatedColumns;
        updatedColumns = updatedColumns.filter((id) => id !== columnId);
      } else {
        updatedColumns.push(columnId);
      }

      const updatedColumnsWithSelection = columnObjectsState.map((column: ColumnData) => {
        if (column.key === columnId) {
          return { ...column, selected: !column.selected };
        }
        return column;
      });

      setColumnObjectsState(updatedColumnsWithSelection);
      setVisibleColumns(updatedColumns);

      return updatedColumns;
    });
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleSaveColumns = () => {
    console.log('Columns saved:', visibleColumns);
  };

  const handleSaveConfig = (config: boolean) => {
    console.log(config);
  };

  const handleReset = () => {
    const sortedColumns = [...columnObjects].sort((a, b) => a.order - b.order);

    const visibleKeys: string[] = sortedColumns.map((col) => col.key).filter((key: string) => key !== 'Info');

    if (JSON.stringify(visibleKeys) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(visibleKeys);
      setIsResetClicked(true);
      setTimeout(() => {
        setIsResetClicked(false);
      }, 1000);
    }
    setAllColumnsForToolbar(visibleColumns);
  };

  const handleApplyClicked = () => {
    setApplyTrigger(true);
    setTimeout(() => {
      setApplyTrigger(false);
    }, 1000);
  };

  return (
    <>
      {sortedColumnObjects && sortedColumnObjects.length > 0 && (
        <TableToolbar
          ref={dataTableRef}
          columns={allColumnsForToolbar
            .filter((columnId) => !fixedColumns.includes(columnId))
            .map((columnId) => ({
              id: columnId,
              Header: getColumnHeader(columnId),
              isVisible: visibleColumns.includes(columnId),
            }))}
          onColumnToggle={handleColumnToggle}
          onSaveColumns={handleSaveColumns}
          onSearch={handleSearch}
          onUpdateColumns={handleUpdateColumns}
          onApplyClicked={() => handleApplyClicked()}
          saveConfigHasbeenClicked={handleSaveConfig}
          resetToDefaultSystemClicked={() => handleReset()}
          onExportXLS={handleExportXLS}
          wellTestTable={false}
          rPermission={rPermission}
          tableName='Real-Time-well-Test'
          disableAllInstallations
        />
      )}
      {isLoading ? (
        <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
          <Loader />
        </div>
      ) : (
        <DataTable
          ref={dataTableRef}
          visibleColumns={[...fixedColumns, ...visibleColumns]}
          searchTerm={searchTerm}
          onUpdateVisibleColumns={setVisibleColumns}
          applyTrigger={applyTrigger}
          isResetClicked={isResetClicked}
          installNumber={selectedInstallNumber}
          columnObjects={columnObjects}
          fixedColumnObjects={fixedColumnObjects}
          allRows={allRows}
        />
      )}
    </>
  );
}
